import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  GraduationCap,
  LockKeyhole,
  Target,
} from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import {
  CURRICULUM_TERM_COUNT,
  LEARN_CURRICULUM,
  LEARN_GROUPS,
} from "@/data/learnCurriculum";
import { getLearningProgress } from "@/lib/api";
import { STUDY } from "@/constants/testIds";
import { useStudyAuth } from "@/context/StudyAuth";

const FREE_LESSON_IDS = new Set(["illinois-licensing", "illinois-license-act"]);

function LessonCard({ lesson, complete, locked }) {
  return (
    <Link
      to={`/study/learn/${lesson.id}`}
      className="group flex min-h-44 flex-col border border-[color:rgba(10,27,51,0.12)] bg-white p-5 transition-colors hover:border-[var(--ibp-navy-950)]"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs font-bold tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
          LESSON {lesson.number}
        </span>
        {locked ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[color:rgba(11,18,32,0.55)]">
            <LockKeyhole className="h-4 w-4" /> Full access
          </span>
        ) : complete ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#15803d]">
            <CheckCircle2 className="h-4 w-4" /> Complete
          </span>
        ) : null}
      </div>
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
        {lesson.outlineSection}
      </p>
      <h2 className="mt-1 text-base font-bold leading-6 text-[var(--ibp-ink)]">{lesson.title}</h2>
      <p className="mt-2 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">{lesson.summary}</p>
      <div className="mt-auto flex items-center justify-between pt-5 text-sm font-semibold text-[var(--ibp-navy-950)]">
        <span className="inline-flex items-center gap-1.5 text-[color:rgba(11,18,32,0.58)]">
          <Clock3 className="h-4 w-4" /> {lesson.duration}
        </span>
        <span className="inline-flex items-center gap-1.5 group-hover:underline">
          {locked ? "Unlock lesson" : "Open lesson"} <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

export default function StudyLearn() {
  const { hasFullAccess } = useStudyAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["learning-progress"],
    queryFn: getLearningProgress,
  });
  const completed = new Set(data?.completed_ids || []);
  const completedCount = completed.size;
  const progressPct = Math.round((completedCount / LEARN_CURRICULUM.length) * 100);

  return (
    <StudyShell testId={STUDY.learnPage}>
      <section className="border-b border-[color:rgba(10,27,51,0.12)] pb-8">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">
          <GraduationCap className="h-4 w-4" /> Illinois Broker Prep learning path
        </p>
        <div className="mt-3 grid gap-6 lg:grid-cols-[minmax(0,1fr)_15rem] lg:items-end">
          <div>
            <h1 className="text-3xl font-bold text-[var(--ibp-ink)]">Learn the rule before you drill it.</h1>
            <p className="mt-3 max-w-2xl text-base leading-7 text-[color:rgba(11,18,32,0.7)]">
              Short, Illinois-first lessons for the exam topics in this platform. Learn the concept,
              review the key terms, then check your understanding with live questions from the active bank.
            </p>
          </div>
          <div className="border border-[color:rgba(10,27,51,0.12)] bg-white p-4">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
                  Learning path
                </p>
                <p className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">
                  {isLoading ? "-" : `${completedCount}/${LEARN_CURRICULUM.length}`}
                </p>
              </div>
              <span className="text-sm font-semibold text-[var(--ibp-navy-950)]">{isLoading ? "" : `${progressPct}%`}</span>
            </div>
            <div className="mt-3 h-2 overflow-hidden bg-[color:rgba(10,27,51,0.08)]">
              <div className="h-full bg-[var(--ibp-gold-500)]" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-px border-b border-[color:rgba(10,27,51,0.12)] bg-[color:rgba(10,27,51,0.12)] sm:grid-cols-3">
        <div className="bg-[var(--ibp-offwhite)] px-5 py-5">
          <BookOpenCheck className="h-5 w-5 text-[var(--ibp-gold-600,#b8860b)]" />
          <h2 className="mt-3 text-sm font-bold text-[var(--ibp-ink)]">Learn it plainly</h2>
          <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">Read the rule, role, or concept behind the exam question.</p>
        </div>
        <div className="bg-[var(--ibp-offwhite)] px-5 py-5">
          <Target className="h-5 w-5 text-[var(--ibp-gold-600,#b8860b)]" />
          <h2 className="mt-3 text-sm font-bold text-[var(--ibp-ink)]">Recall the vocabulary</h2>
          <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">Each lesson gives you the terms that make the rule easier to recognize.</p>
        </div>
        <div className="bg-[var(--ibp-offwhite)] px-5 py-5">
          <CheckCircle2 className="h-5 w-5 text-[var(--ibp-gold-600,#b8860b)]" />
          <h2 className="mt-3 text-sm font-bold text-[var(--ibp-ink)]">Apply it under pressure</h2>
          <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">Finish with a five-question checkpoint from your question bank.</p>
        </div>
      </section>

      <div className="mt-8 space-y-10">
        {LEARN_GROUPS.map((group) => {
          const lessons = LEARN_CURRICULUM.filter((lesson) =>
            lesson.category.toLowerCase().startsWith(group.id)
          );
          return (
            <section key={group.id}>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ibp-gold-600,#b8860b)]">{group.eyebrow}</p>
                  <h2 className="mt-1 text-xl font-bold text-[var(--ibp-ink)]">{group.title}</h2>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">{group.description}</p>
                </div>
                <p className="text-sm text-[color:rgba(11,18,32,0.55)]">{lessons.length} lessons</p>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-2">
                {lessons.map((lesson) => (
                  <LessonCard
                    key={lesson.id}
                    lesson={lesson}
                    complete={completed.has(lesson.id)}
                    locked={!hasFullAccess && !FREE_LESSON_IDS.has(lesson.id)}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <aside className="mt-10 border-l-4 border-l-[var(--ibp-gold-500)] bg-white px-5 py-4">
        <p className="text-sm font-semibold text-[var(--ibp-ink)]">Built to grow with your study library</p>
        <p className="mt-1 text-sm leading-6 text-[color:rgba(11,18,32,0.65)]">
          This learning path covers the active Illinois, national, and math foundations, with {CURRICULUM_TERM_COUNT} lesson key terms. The Word Bank and active question library will continue to expand with future updates.
        </p>
        <p className="mt-2 text-xs leading-5 text-[color:rgba(11,18,32,0.5)]">
          Study guidance only. Use the linked sources and your required pre-license materials for current rules.
        </p>
      </aside>
    </StudyShell>
  );
}
